import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Loader2, FolderOpen } from "lucide-react";
import { fetchCockpitDossierDetail, type CockpitDossierDetail as DossierDetailType } from "../../lib/admin";
import { supabase } from "../../lib/supabase";
import { useDossierStore } from "../../store/dossierStore";
import StatCard from "../../components/cockpit/StatCard";

function fmtDate(s: string | null | undefined): string {
  if (!s) return "—";
  try {
    return new Date(s).toLocaleString("fr-FR", { dateStyle: "short" });
  } catch {
    return "—";
  }
}

export default function CockpitDossierDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const setActiveDossier = useDossierStore((s) => s.setActiveDossier);
  const [data, setData] = useState<DossierDetailType | null | undefined>(undefined);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    let alive = true;
    fetchCockpitDossierDetail(id)
      .then((d) => {
        if (alive) setData(d);
      })
      .catch(() => {
        if (alive) setErr("Chargement impossible.");
      });
    return () => {
      alive = false;
    };
  }, [id]);

  const accederAuDossier = async () => {
    if (!id) return;
    const { data: row, error } = await supabase
      .from("dossiers")
      .select("id, nom, cabinet_id")
      .eq("id", id)
      .maybeSingle();
    if (error || !row) return;
    setActiveDossier({
      id: row.id,
      nom: row.nom,
      cabinet_id: row.cabinet_id ?? undefined,
    });
    navigate("/dashboard");
  };

  if (err) {
    return <p className="text-sm text-red-400">{err}</p>;
  }
  if (data === undefined) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
      </div>
    );
  }
  if (!data) {
    return <p className="text-slate-400">Dossier introuvable.</p>;
  }

  const { dossier: d, membres, stats, infos } = data;
  const di = infos as Record<string, string | null | undefined> | null;

  return (
    <div className="mx-auto max-w-4xl space-y-8">
      <Link
        to="/cockpit/dossiers"
        className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-orange-400"
      >
        <ArrowLeft size={16} /> Retour à la liste
      </Link>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-slate-900">{d.nom}</h2>
          <p className="mt-1 text-sm text-slate-400">
            {d.pays || "—"} · {d.regime_fiscal || "—"} · {d.type_comptabilite || "—"}
          </p>
          <p className="mt-1 text-xs text-slate-500">Créé le {fmtDate(d.created_at)}</p>
        </div>
        <button
          type="button"
          onClick={accederAuDossier}
          className="inline-flex items-center gap-2 rounded-lg bg-orange-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-orange-500"
        >
          <FolderOpen size={18} /> Accéder au dossier
        </button>
      </div>

      <div className="grid grid-cols-1 gap-px overflow-hidden rounded-lg border border-[#e4e8ef] bg-[#e4e8ef] sm:grid-cols-3">
        <StatCard inGrid accent title="Écritures" value={stats.nb_ecritures} />
        <StatCard inGrid title="Factures vente" value={stats.nb_factures_vente} />
        <StatCard inGrid title="Factures achat" value={stats.nb_factures_achat} />
      </div>

      <div className="rounded-lg border border-[#e4e8ef] bg-white p-5">
        <h3 className="text-[13px] font-semibold text-slate-900">Entreprise (dossiers_infos)</h3>
        {di ? (
          <dl className="mt-4 grid gap-2 text-sm sm:grid-cols-2">
            <div>
              <dt className="text-[11px] text-slate-400">Dénomination</dt>
              <dd className="text-slate-800">{di.denomination || "—"}</dd>
            </div>
            <div>
              <dt className="text-[11px] text-slate-400">NIU</dt>
              <dd className="text-slate-800">{di.niu || "—"}</dd>
            </div>
            <div>
              <dt className="text-[11px] text-slate-400">E-mail</dt>
              <dd className="text-slate-800">{di.email || "—"}</dd>
            </div>
            <div>
              <dt className="text-[11px] text-slate-400">Téléphone</dt>
              <dd className="text-slate-800">{di.telephone || "—"}</dd>
            </div>
          </dl>
        ) : (
          <p className="mt-3 text-sm text-slate-500">Pas encore renseigné.</p>
        )}
      </div>

      <div className="rounded-lg border border-[#e4e8ef] bg-white p-5">
        <h3 className="text-[13px] font-semibold text-slate-900">Propriétaire cabinet</h3>
        <p className="mt-2 text-sm text-slate-600">{d.owner_email || "—"}</p>
        <Link
          to={`/cockpit/users/${d.owner_user_id}`}
          className="mt-2 inline-block text-sm text-orange-400 hover:text-orange-300"
        >
          Voir le profil utilisateur
        </Link>
      </div>

      <div className="rounded-lg border border-[#e4e8ef] bg-white p-5">
        <h3 className="text-[13px] font-semibold text-slate-900">Membres</h3>
        {membres.length === 0 ? (
          <p className="mt-3 text-sm text-slate-500">Aucun membre (hors lien cabinet).</p>
        ) : (
          <ul className="mt-4 divide-y divide-[#e4e8ef]">
            {membres.map((m) => (
              <li key={m.user_id} className="flex flex-wrap items-center justify-between gap-2 py-3 text-sm">
                <div>
                  <span className="font-medium text-slate-800">{m.nom_complet || m.email}</span>
                  <span className="ml-2 text-slate-500">{m.email}</span>
                </div>
                <span className="inline-flex items-center rounded-sm bg-[#eef1f7] px-1.5 py-px text-[10px] font-semibold tracking-[0.02em] text-[#1a2744]">
                  {m.role}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
